/* ─────────────────────────────────────────────────────────────
   StackPricer — drop into src/features/stack/StackPricer.jsx

   Standalone: no imports from your existing features. Wire your
   real price lookup into `priceStack` (marked TODO) and it works.

   Needs pokepricer-theme.css imported (see handoff instructions).
   ───────────────────────────────────────────────────────────── */

import React, { useState, useMemo, useCallback } from "react";
import { fetchCardsByIds, findCards, normalizeFromPPT, cardNumberMatches, fetchOnePiecePrices } from "../../shared";
import { asItem } from "../cards/CardFinder";
import { searchOnePiece, baseCode, printLabel, printsOf } from "../onepiece/onePieceIndex";
import { opImageUrl, DON_ART } from "../onepiece/onePieceCards";
import { knownDon, loadDon } from "../onepiece/onePieceDon";
import { CardZoom, BulkFilter, isBulk, loadBulkPref, saveBulkPref, useUndo, UndoBar } from "../../ui";
import StackBuilder from "./StackBuilder";

// The range people actually deal at. Typing into the big number still accepts
// anything, so the presets don't have to cover the unusual cases.
const PRESETS = [70, 75, 80, 85, 90];
const PCT_MIN = 70;
const PCT_MAX = 90;

/**
 * Keep a typed number inside its bounds.
 *
 * The `min` and `max` attributes on a number input govern its spinner and its
 * form validation, and neither of those is involved when somebody types. So
 * fees took 150 and shipping took -20, and negative fees paid out more than
 * the cards were worth — gross $6.12, "you keep $16.94".
 */
/**
 * "1 CARD", "2 CARDS", "1 card", "2 cards".
 *
 * The plural follows the case of the word it is made from, because this is
 * used both in the shouting labels and in a sentence — a fixed "S" produced
 * "Clear 2 cardS?" in a button somebody actually reads.
 */
const plural = (n, one, many) => {
  const rest = many || (one === one.toUpperCase() ? `${one}S` : `${one}s`);
  return `${n} ${n === 1 ? one : rest}`;
};

/** A stored number, or the default — not zero, which is a price. */
const num = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const clamp = (value, low, high) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return low;
  return Math.min(high, Math.max(low, n));
};

/**
 * The stack, kept between visits.
 *
 * It was the one thing in the app that did not survive a reload — the
 * collection, the lists, the goals and the percentage all did — which made an
 * accidental swipe on a phone with eighty cards on the table cost all eighty.
 * The menu already promised "it only lives in this browser", which reads as a
 * promise that it lives there at all.
 *
 * One entry per game rather than one stack. Switching games used to empty it
 * with no warning, because the screen is remounted per game; kept apart, each
 * game simply keeps its own pile and switching back finds it where it was.
 *
 * Deliberately not part of the account sync. A stack is a working surface —
 * the cards in front of you right now — and two devices silently overwriting
 * each other's would be worse than each keeping its own.
 */
/** The priced row a hand-added card makes. Built in one place because it is
 *  wanted in two: the moment it is added, and every time the stack is priced
 *  again — which is what keeps it from vanishing on the second press. */
const rowForManual = (i) => ({
  id: `row-${i.key}`,
  itemKey: i.key,
  cardId: i.key,
  name: i.name,
  set: "Your price",
  number: i.number || "",
  image: "",
  qty: i.qty,
  market: i.price,
  pct: null,
  manual: true,
});

const STACK_KEY = "ppt_stack_v1";

/**
 * How you sell, kept between visits.
 *
 * Once the stack itself survived a reload, coming back to your cards priced at
 * a percentage you did not choose, with your fees silently reset to zero, was
 * a worse answer than losing them — the number on screen looked like a real
 * one. These are one setting for both games: what you take off the top is
 * about who you sell to, not what you sell.
 *
 * Not the collection's ppt_percentage_v1, which is a different figure for a
 * different screen and was never read here.
 */
const PREFS_KEY = "ppt_stack_prefs_v1";

const readPrefs = () => {
  try {
    const held = JSON.parse(window.localStorage.getItem(PREFS_KEY));
    return held && typeof held === "object" ? held : {};
  } catch {
    return {};
  }
};

const writePrefs = (prefs) => {
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* a blocked store forgets the profile, it does not break the sale */
  }
};

const readStacks = () => {
  try {
    const held = JSON.parse(window.localStorage.getItem(STACK_KEY));
    return held && typeof held === "object" && !Array.isArray(held) ? held : {};
  } catch {
    return {};   // a corrupt copy is no copy
  }
};

/**
 * One card out of storage, or nothing.
 *
 * Malformed JSON was already handled and malformed *contents* were not, which
 * is a distinction storage does not make: a single null in the array threw on
 * the first render and took the whole app down. Persistence turned that from a
 * bad afternoon into a permanent one — the reload that used to fix everything
 * became the thing that re-ran the crash, and the only way out was clearing
 * site data, which takes the collection and the lists with it.
 *
 * Nothing here trusts a field. The stack is written by this app, but "written
 * by this app" includes every older version of it and anything that has been
 * through a sync, an export, an import, or a text editor.
 */
const cleanItem = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const key = typeof raw.key === "string" && raw.key ? raw.key : null;
  const name = typeof raw.name === "string" ? raw.name : "";
  if (!key || !name) return null;   // nothing identifiable enough to show

  /* A quantity is a whole number of cards you could physically hold. A missing
     one drew a blank stepper, a negative one drew "-5", and 1e9 drew all ten
     digits across the row. */
  const qty = Math.min(999, Math.max(1, Math.floor(num(raw.qty, 1)) || 1));

  const item = {
    key,
    name,
    number: typeof raw.number === "string" ? raw.number : "",
    set: typeof raw.set === "string" ? raw.set : "",
    image: typeof raw.image === "string" ? raw.image : "",
    qty,
  };
  if (raw.unpriced) item.unpriced = true;

  /* A hand-priced card without a price is not a hand-priced card. Dropping the
     flag turns it back into one to look up, which is recoverable; keeping it
     would put a card of unknown value into the total. */
  if (raw.manual) {
    const price = num(raw.price, NaN);
    if (Number.isFinite(price) && price > 0) {
      item.manual = true;
      item.price = price;
    } else if (item.set === "Your price") {
      /* "Your price" is the label a hand-priced card wears. Losing the flag
         and keeping the label left an ordinary card whose set was the string
         "Your price" — the leftovers of a state it is no longer in. */
      item.set = "";
    }
  }
  return item;
};

const readStack = (game) => {
  const held = readStacks()[game];
  if (!Array.isArray(held)) return [];
  return held.map(cleanItem).filter(Boolean);
};

/** @returns false if the stack could not be stored, so somebody can be told. */
const writeStack = (game, items) => {
  try {
    const all = readStacks();
    if (items.length) all[game] = items;
    else delete all[game];
    window.localStorage.setItem(STACK_KEY, JSON.stringify(all));
    return true;
  } catch {
    /* A blocked or full store loses the stack on reload rather than now — and
       the whole reason the stack is stored is somebody who cannot afford to
       lose it. Swallowing this would be the same silence that let a broken
       sync run for a day. */
    return false;
  }
};

// Cash clears shipping as well as the fee; the other two only touch the fee.
const FEE_PRESETS = [
  { label: "TCG 12.6%",  fees: 12.6 },
  { label: "eBay 13.6%", fees: 13.6 },
  { label: "Cash 0%",    fees: 0, shipping: 0 },
];

const usd = (n) =>
  "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });


/* Parses "Umbreon VMAX 215" / "Charizard VMAX 020 x2" into
   { name, number, qty }. Trailing xN sets quantity. */
function parseLine(line) {
  const raw = line.trim();
  if (!raw) return null;
  let qty = 1;
  const qtyMatch = raw.match(/\s+x\s*(\d+)$/i);
  const body = qtyMatch ? raw.slice(0, qtyMatch.index) : raw;
  if (qtyMatch) qty = parseInt(qtyMatch[1], 10) || 1;
  const numMatch = body.match(/\s+(\d+[a-z]?)$/i);
  return {
    name: (numMatch ? body.slice(0, numMatch.index) : body).trim(),
    number: numMatch ? numMatch[1] : "",
    qty,
  };
}

/* Not every card is in the price source — an obscure promo, something too new
   to be listed, a sealed extra, a card from a game we don't cover at all. This
   puts one in the stack at a price you set, without pretending to look it up.
   Its own component so a half-typed entry doesn't re-render the table. */
function ManualCard({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState("");

  const value = Number(price);
  const count = Math.max(1, Math.min(99, Number(qty) || 1));
  const valid = name.trim() !== "" && Number.isFinite(value) && value > 0;

  const submit = () => {
    if (!valid) return;
    onAdd({ name: name.trim(), qty: count }, value);
    setName("");
    setQty("1");
    setPrice("");
  };

  if (!open) {
    return (
      <button className="btn btn-secondary stack-manual-open" onClick={() => setOpen(true)}>
        + Add a card manually
      </button>
    );
  }

  return (
    <div className="stack-manual-form">
      <div className="stack-label">A CARD THE PRICE SOURCE DOESN'T HAVE</div>
      <div className="stack-manual-fields">
        <input
          autoFocus
          className="stack-manual-name"
          type="text"
          placeholder="What is it?"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          aria-label="Card name"
        />
        <input
          className="stack-manual-qty"
          type="number" min={1} max={99}
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          aria-label="Quantity"
        />
        <span className="stack-manual-money">
          $
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={price}
            onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ""))}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            aria-label="Your price"
          />
        </span>
      </div>
      <div className="stack-manual-actions">
        <button
          className="btn btn-primary btn-sm"
          onClick={submit}
          disabled={!valid}
          title={valid ? `Add ${count} at ${usd(value)} each` : "Needs a name and a price above zero"}
        >
          Add to stack
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => { setOpen(false); setName(""); setQty("1"); setPrice(""); }}
        >
          Done
        </button>
      </div>
    </div>
  );
}

/* The sample stack has gone with the box it sat in. It existed to teach a line
   format, and there is no line format left to teach — the screen opens empty
   and says where cards come from instead. */

/* How this works, per game. Worth spelling out because the two differ in a
   way that is not guessable: a Pokemon name plus number names one card, while
   a One Piece name alone names dozens — "Monkey.D.Luffy" is 129 of them — so
   the same input is precise in one game and near-meaningless in the other. */
function FormatHelp({ isOnePiece }) {
  return (
    <div className="stack-help" id="stack-format-help">
      <p className="stack-help-line">
        Search a card below and press it. It joins the stack above, and the search
        stays where it is so you can keep going.
      </p>
      <p className="stack-help-line">
        <strong>+</strong> and <strong>−</strong> set how many you have.
        Minus at one takes the card out.
      </p>
      {isOnePiece ? (
        <p className="stack-help-line">
          Search by name or by the code at the bottom of the card —
          <code> OP13-118</code>. Alt arts share a number and can differ by three
          hundred times, so the art beside each result is what tells them apart.
        </p>
      ) : (
        <>
          <p className="stack-help-line">
            Search by name. Add the number under the art to land on one print —
            <code> Umbreon VMAX 215</code>. The number is the part before the
            slash, and codes like <code>TG23</code> work the same way.
          </p>
          {/* The set is printed on the card, so it is a natural thing to type,
              and until recently typing it found nothing at all. */}
          <p className="stack-help-line">
            Or name the set instead of the number —
            <code> Umbreon prismatic</code>. Part of the set's name is enough,
            and a set on its own — <code>Prismatic Evolutions</code> — lists it.
          </p>
        </>
      )}
      <p className="stack-help-line stack-help-dim">
        A card the search cannot find can be added with
        <strong> + Add a card manually</strong>, at a price you set.
      </p>
    </div>
  );
}

export default function StackPricer({ isOnePiece = false, devUnlocked = false }) {
  /* The cards you have picked are the stack. The text the pricer parses is
     built from them rather than typed — which is the whole change here: a line
     of text has to be resolved and hoped about, a picked card is already a
     card. Kept as text underneath so everything downstream is untouched. */
  const stackGame = isOnePiece ? "onepiece" : "pokemon";
  const [items, setItems] = useState(() => readStack(stackGame));

  /* Saved as it changes rather than on the way out: there is no "on the way
     out" for a tab that is closed, a phone that sleeps, or the swipe this is
     really about. */
  const [saveFailed, setSaveFailed] = useState(false);
  React.useEffect(() => {
    setSaveFailed(!writeStack(stackGame, items));
  }, [stackGame, items]);

  /* Said once, then gone. A stack that changed under you for a reason you
     cannot see is the complaint; a line that stays on screen afterwards is a
     different one. */
  const [adopted, setAdopted] = useState(false);
  React.useEffect(() => {
    if (!adopted) return undefined;
    const t = setTimeout(() => setAdopted(false), 4000);
    return () => clearTimeout(t);
  }, [adopted]);

  /* One stack per device, and every tab looking at it.
   *
   * Two tabs each kept their own copy and each wrote the whole thing, so the
   * second one to touch it erased the first — silently, with the losing tab
   * still showing cards that were already gone from disk and only finding out
   * on reload. Two tabs used to be two scratchpads with nothing shared to
   * lose; storing the stack is what gave them something.
   *
   * Adopting rather than merging. A merge cannot tell a card somebody removed
   * from a card the other tab has not seen yet, so it resurrects deletions —
   * and being shown the truth is the part that was missing. */
  React.useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STACK_KEY) return;
      const theirs = readStack(stackGame);
      setItems((mine) => {
        const same =
          mine.length === theirs.length &&
          mine.every((it, n) => it.key === theirs[n].key && it.qty === theirs[n].qty);
        if (same) return mine;
        /* Saying so. The policy is right and it was invisible: clear the stack
           in one tab and the other one empties for no reason anybody in front
           of it could see. Correct is not the same as legible. */
        setAdopted(true);
        return theirs;
      });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [stackGame]);
  /* The DON!! cards, so a stack holding one can be priced. Loaded here rather
     than looked up per line: the whole point of the One Piece path is that it
     resolves a stack with no request per card. */
  const [don, setDon] = useState(knownDon);
  React.useEffect(() => {
    if (!isOnePiece) return undefined;
    let live = true;
    // Empty means the source was unreachable, which is nothing new to render.
    loadDon().then((d) => { if (live && d.length) setDon(d); });
    return () => { live = false; };
  }, [isOnePiece]);
  /**
   * What the pricer works from: the stack itself, one line per card.
   *
   * This used to be built as text and immediately parsed back — a picked card
   * was flattened into "Umbreon VMAX 215" and then read apart again. That cost
   * nothing in accuracy but it threw away the one thing worth keeping, which is
   * which card in the stack each priced row came from. Without that link the
   * two lists could not be kept in step: pressing × on a row hid it while the
   * card stayed in the stack, and taking a card out of the stack left its row
   * and its money on screen.
   */
  const priceLines = useMemo(
    () =>
      items
        /* A hand-priced card is in the stack but is not looked up — its price
           was given, not found, and searching for it would either fail or,
           worse, find something else and quietly overwrite the figure. */
        .filter((i) => !i.manual)
        .map((i) => ({
          /* A One Piece code names one print outright, so it stands as the
             whole line; a Pokemon name cannot identify a print without its
             number beside it. */
          name: isOnePiece && i.number ? i.number : i.name,
          number: isOnePiece && i.number ? "" : i.number || "",
          qty: i.qty,
          itemKey: i.key,
        })),
    [items, isOnePiece]
  );

  /**
   * Can this print actually be priced?
   *
   * The search and the pricer read the same catalogue under different rules:
   * search offers every card of that name, pricing drops anything the market
   * has never listed. So Umbreon VMAX TG23 was offered, added, and then
   * reported as "not a card" — which it is, and always was. Asking at the
   * moment it goes in turns that into something said in time to matter.
   */
  const checkPriced = useCallback(
    async (item) => {
      try {
        if (isOnePiece) {
          const prices = await fetchOnePiecePrices([item.number]);
          return Number.isFinite(Number(prices?.[item.number]));
        }
        /* By id, through the same per-id cache the batch reads — so the check
           made when a card goes in is the price already waiting when the
           stack is priced. It used to be a fuzzy search by name, which could
           say yes about a different print of the same name. */
        const { cards, unavailable } = await fetchCardsByIds([item.key]);
        // A source that could not be asked says nothing about the card.
        if (unavailable.includes(item.key)) return true;
        const card = cards[item.key] ? normalizeFromPPT(cards[item.key]) : null;
        return Boolean(card) && Number(card.prices?.["Near Mint"]?.price ?? card.marketPrice) > 0;
      } catch {
        // A lookup that failed says nothing about the card, so it says nothing.
        return true;
      }
    },
    [isOnePiece]
  );

  const addItem = useCallback(
    (item) => {
      setItems((prev) => {
        const at = prev.findIndex((i) => i.key === item.key);
        if (at === -1) return [...prev, { ...item, qty: 1 }];
        // Adding one you already have is asking for a second copy, not a duplicate row.
        const next = [...prev];
        next[at] = { ...next[at], qty: next[at].qty + 1 };
        return next;
      });

      /* After it is on screen, not before. The card appears the instant it is
         pressed; whether anyone will pay for it is a slower question. */
      checkPriced(item).then((ok) => {
        if (ok) return;
        setItems((prev) => prev.map((i) => (i.key === item.key ? { ...i, unpriced: true } : i)));
      });
    },
    [checkPriced]
  );

  const bumpItem = useCallback((key, delta) => {
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, qty: i.qty + delta } : i))
        .filter((i) => i.qty > 0)
    );
  }, []);
  /* Falling back to the default rather than to zero. clamp() treats anything
     unreadable as its floor, which for a percentage is 0 — so one corrupt
     pref priced an entire stack at nothing and looked deliberate doing it. */
  const [pct, setPct] = useState(() => clamp(num(readPrefs().pct, 80), 0, 200));
  /* What is in the box, which is not always a number yet. "12." is a real
     thing to be halfway through typing and cannot be held as one, and the box
     used to strip everything that was not a digit — so 12.5 became 125, a
     tenfold overprice arrived at silently and in the direction where the
     cards do not sell. */
  const [pctText, setPctText] = useState(() => String(clamp(num(readPrefs().pct, 80), 0, 200)));
  const applyPct = useCallback((n) => { setPct(n); setPctText(String(n)); }, []);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // % taken off the top (TCG ~12.6, eBay ~13.6), and a flat $ off the total.
  const [fees, setFees] = useState(() => clamp(num(readPrefs().fees, 0), 0, 100));
  const [shipping, setShipping] = useState(() => clamp(num(readPrefs().shipping, 0), 0, Infinity));

  /* Written as they change. All three together, because they are one answer to
     "how am I selling this" and remembering two of them would be its own kind
     of wrong. */
  React.useEffect(() => {
    writePrefs({ pct, fees, shipping });
  }, [pct, fees, shipping]);
  const [error, setError] = useState("");
  const [unmatched, setUnmatched] = useState([]);   // lines that found no card
  const [zoom, setZoom] = useState(null);           // row whose art is expanded
  const [bulk, setBulk] = useState(loadBulkPref);   // { hide, under }
  const [picking, setPicking] = useState(null);     // row whose print is being chosen
  const [helpOpen, setHelpOpen] = useState(false);

  /* Clearing, with a way back. The whole stack and its priced rows go
     together, and come back together — half a stack restored would be a
     stack that disagrees with its own table, which is the bug from two
     rounds ago wearing a new hat. */
  const undo = useUndo();
  const clearStack = () => {
    const wasItems = items;
    const wasRows = rows;
    setItems([]); setRows([]); setError(""); setUnmatched([]);
    undo.offer(plural(wasItems.length, "card"), () => {
      setItems(wasItems);
      setRows(wasRows);
    });
  };
  // Dev-only, and off by default: pasting a list is how this screen used to
  // work, and it is still the fastest way in with forty cards on the table.
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteDraft, setPasteDraft] = useState("");

  /* A pasted list becomes picked cards, so there is still only one source of
     truth — and "picked" means resolved to a real card with an id, the same
     way pressing one in the search does. Pricing is by id now, so a line that
     stayed as text would be a line that could never be priced. Each line goes
     through the same cheap, cached finder the picker uses; one the finder
     cannot place stays as typed and is reported when the stack is priced. */
  const loadPaste = useCallback(async () => {
    const parsed = pasteDraft.split("\n").map(parseLine).filter(Boolean);
    if (!parsed.length) return;
    setPasteDraft("");
    setPasteOpen(false);

    const resolved = await Promise.all(
      parsed.map(async (p) => {
        const fallback = {
          key: `${p.name} ${p.number}`.trim().toLowerCase(),
          name: p.name, number: p.number, set: "", image: null, qty: p.qty,
        };
        if (isOnePiece) return fallback;   // the code is the id; priceStack resolves it
        try {
          const found = (await findCards(`${p.name} ${p.number}`.trim())) || [];
          const hit = p.number
            ? found.find((c) => cardNumberMatches(p.number, c.cardNumber))
            : found[0];
          return hit ? { ...asItem(hit, false), qty: p.qty } : fallback;
        } catch {
          return fallback;
        }
      })
    );

    setItems((prev) => {
      const next = [...prev];
      for (const item of resolved) {
        const at = next.findIndex((i) => i.key === item.key);
        if (at === -1) next.push(item);
        else next[at] = { ...next[at], qty: next[at].qty + item.qty };
      }
      return next;
    });
  }, [pasteDraft, isOnePiece]);

  // Escape closes the expanded art, matching every other overlay in the app.
  React.useEffect(() => {
    if (!zoom) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setZoom(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [zoom]);

  async function priceStack() {
    /* No merging pass any more. It existed because a pasted list repeats the
       same card on separate lines; a pasted list becomes picked cards now, and
       picking one you already have raises its count instead of adding a second
       entry — so the stack arrives here already one line per card. */
    const parsed = priceLines;
    if (!parsed.length) return;

    setLoading(true);
    setError("");
    setUnmatched([]);

    /* One Piece resolves against a catalogue bundled into the app, so naming a
       card costs nothing and works offline, and the whole stack prices in a
       single call. It is the cheaper half of the app by a wide margin — the
       Pokemon path below needs a round trip per line. */
    if (isOnePiece) {
      try {
        const found = [];
        const missedOp = [];
        /* A DON!! card is in no catalogue, so searching for it can only fail.
           It arrives as its own id — the line is the id, since a One Piece
           item is serialised to one — and that id is already the answer. */
        const donById = new Map(don.map((d) => [d.id.toLowerCase(), d]));
        for (const p of parsed) {
          const asDon = donById.get(String(p.name || "").trim().toLowerCase());
          if (asDon) {
            found.push({ line: p, hit: { id: asDon.id, name: asDon.name }, don: asDon });
            continue;
          }
          /* A pasted code arrives whole in `name`, since it carries no space —
             "OP13-118_p2" names one print outright. A typed name and number
             arrive apart, so the name finds the card and the number picks the
             print out of it. Searching the bare number would be far too loose:
             "022" appears inside hundreds of unrelated codes. */
          let hits = searchOnePiece([p.name, p.number].filter(Boolean).join(" ").trim()) || [];
          if (!hits.length && p.number) hits = searchOnePiece(p.name.trim()) || [];
          if (p.number && hits.length) {
            const want = String(parseInt(p.number, 10));
            const narrowed = hits.filter((h) => {
              const printed = baseCode(h.id).split("-")[1];
              return printed && String(parseInt(printed, 10)) === want;
            });
            if (narrowed.length) hits = narrowed;
          }
          if (!hits.length) { missedOp.push(p); continue; }
          found.push({ line: p, hit: hits[0] });
        }
        /* A bare code names a card, not a print, and the prints of one card can
           differ by 300x. Rather than quietly taking the first, every print of
           every matched card is priced in the same single call, so the row can
           say what it chose and offer the alternatives. */
        /* A DON card is a family of one: there are no other prints of a card
           that has no number. It still goes through the same single price
           call, which answers DON ids alongside ordinary codes. */
        const families = found.map((f) => ({
          ...f,
          prints: f.don ? [{ id: f.don.id, rarity: "DON!!" }] : printsOf(f.hit.id),
        }));
        const allIds = [...new Set(families.flatMap((f) => f.prints.map((p) => p.id)))];
        const prices = allIds.length ? await fetchOnePiecePrices(allIds) : {};

        setRows([
          ...families.map((f, i) => {
            const prints = f.prints.map((p) => ({
              id: p.id,
              // A DON card's label is the whole thing it is called, and its art
              // is hosted rather than built from a code it does not have.
              label: f.don ? f.don.label : printLabel(p.id, p.rarity),
              market: Number(prices[p.id]) || 0,
              image: f.don ? f.don.image || DON_ART : opImageUrl(p.id),
            }));
            const chosen = prints.find((p) => p.id === f.hit.id) || prints[0];
            return {
              id: `${f.hit.id}-${i}`,
              // Which card in the stack this row is about, so the two can be
              // kept in step rather than drifting apart.
              itemKey: f.line.itemKey,
              cardId: chosen.id,
              name: f.hit.name,
              set: chosen.label,
              // Nothing is printed on a DON card, so the set stands in for it.
              number: f.don ? f.don.set || "DON!!" : baseCode(f.hit.id),
              image: chosen.image,
              qty: f.line.qty,
              market: chosen.market,
              pct: null,
              prints,
            };
          }),
          ...manualRows(),
        ]);
        setUnmatched(missedOp);
        if (!found.length && !manualCount && missedOp.length) {
          setError("Nothing in the stack has a market price.");
        }
      } catch (e) {
        setError(`Pricing stopped: ${e?.response?.data?.message || e.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    /* One request for the whole stack, by id.
     *
     * This was one search per card, run one at a time. Two things were wrong
     * with it. Sixty cards was sixty requests inside a minute, which is the
     * Worker's own per-IP ceiling — a stack big enough to be worth pricing got
     * 429s partway through. And each was a fuzzy search by name and number for
     * a card whose exact id the stack was already holding, having picked it
     * from a search; re-deriving the print from text is how "Charizard ex 199"
     * once came back as 006. The id was the answer all along. */
    const priced = [];
    const missed = [];
    try {
      const { cards: byId, unavailable } = await fetchCardsByIds(parsed.map((p) => p.itemKey));
      const couldNotAsk = new Set(unavailable);
      for (const p of parsed) {
        /* Not a miss. The source could not be asked about this card just now,
           and "no market price — add it manually" would send somebody off to
           hand-price a card that has a perfectly good one. It is said
           separately, below, and the row is simply absent until next time. */
        if (couldNotAsk.has(p.itemKey)) continue;

        const raw = byId[p.itemKey];
        const hit = raw ? normalizeFromPPT(raw) : null;

        if (!hit) {
          // Kept as the parsed line, not a string: a line that found nothing
          // can still be priced by hand, and that needs its name and quantity.
          missed.push(p);
          continue;
        }
        priced.push({
          id: `${hit.id}-${priced.length}`,
          itemKey: p.itemKey,
          name: hit.name,
          set: hit.setName || "",
          number: hit.number || p.number,
          image: hit.imageUrl || "",
          qty: p.qty,
          market: Number(hit.prices?.["Near Mint"]?.price ?? hit.marketPrice) || 0,
          pct: null, // null = follow the stack percentage
        });
      }
      setRows([...priced, ...manualRows()]);
      setUnmatched(missed);
      if (couldNotAsk.size) {
        const n = couldNotAsk.size;
        setError(
          `Couldn't reach the price source for ${n} card${n === 1 ? "" : "s"} just now — ` +
          "press Price the stack again in a minute to add them."
        );
      } else if (!priced.length && !manualCount && missed.length) {
        setError("Nothing in the stack has a market price.");
      }
    } catch (e) {
      const status = e?.response?.status;
      // Whatever was priced before it failed, and the hand-priced cards, which
      // never depended on the source that just went down.
      setRows([...priced, ...manualRows()]);
      setError(
        status === 429
          ? "Out of API credits or rate limited — the daily allowance resets at 00:00 UTC."
          : `Pricing stopped: ${e?.response?.data?.message || e.message}`
      );
    } finally {
      setLoading(false);
    }
  }

  const rowPct = (r) => (r.pct == null ? pct : r.pct);

  const changeBulk = (next) => { setBulk(next); saveBulkPref(next); };

  // Swapping which print a row is about: the identity, the art and the price
  // all move together, and the total follows.
  const choosePrint = (rowId, printId) =>
    setRows((rs) =>
      rs.map((r) => {
        if (r.id !== rowId) return r;
        const p = r.prints?.find((x) => x.id === printId);
        return p ? { ...r, cardId: p.id, set: p.label, image: p.image, market: p.market } : r;
      })
    );

  /* A card given a value by hand joins the stack as a normal row and counts in
     the total. Marked as yours everywhere it appears, so the total can never
     claim a typed number came from market data. */
  const addManual = (line, price) => {
    const value = Number(price);
    if (!Number.isFinite(value) || value <= 0) return;
    /* Into the stack, not straight into the results.
     *
     * It used to be pushed onto the priced rows alone, which meant it was in
     * the table and not in the stack: the header counted one thing and the
     * table another, the card had no quantity control and no way to be taken
     * out, and the next press of "Price the stack" rebuilt the rows from the
     * stack and took it away again with nothing said. A card you added by
     * hand is a card in the stack; there is only the one list now. */
    /* The same card at the same price, added twice, is two of it — the same
       rule the search already follows. Two entries would also share a name and
       everything else, which is how the one-name-two-controls problem came
       back from this side; there is nothing to tell them apart because there
       is no difference. */
    const twin = items.find(
      (i) => i.manual && i.name === line.name && i.number === (line.number || "") && i.price === value
    );
    if (twin) {
      const qty = twin.qty + (line.qty || 1);
      setItems((prev) => prev.map((i) => (i.key === twin.key ? { ...i, qty } : i)));
      setRows((rs) => rs.map((r) => (r.itemKey === twin.key ? { ...r, qty } : r)));
      setUnmatched((u) => u.filter((m) => m.name.toLowerCase() !== line.name.toLowerCase()));
      return;
    }

    const item = {
      key: `manual-${Date.now()}-${items.length}`,
      name: line.name,
      number: line.number || "",
      set: "Your price",
      image: "",
      qty: line.qty || 1,
      manual: true,
      price: value,
    };
    setItems((prev) => [...prev, item]);
    // And into the table at once. Its price needs no looking up, so making
    // somebody press "Price the stack" to see a figure they just typed would
    // be asking them to wait for arithmetic.
    setRows((rs) => [...rs, rowForManual(item)]);
    // A hand-added card answers whichever unmatched line was about it.
    setUnmatched((u) => u.filter((m) => m.name.toLowerCase() !== line.name.toLowerCase()));
  };

  /* Rebuilt from the stack every time the stack is priced, which is what keeps
     these from being wiped by the rebuild. */
  const manualRows = useCallback(
    () => items.filter((i) => i.manual).map(rowForManual),
    [items]
  );

  const manualCount = items.filter((i) => i.manual).length;

  // Hidden bulk is left out of the totals too. A number that quietly includes
  // cards you can't see is worse than no filter at all — what's on screen is
  // what you're offering on, and the count of what's set aside is shown.
  const { shown, bulkRows, bulkValue } = useMemo(() => {
    if (!bulk.hide) return { shown: rows, bulkRows: [], bulkValue: 0 };
    const keep = [], drop = [];
    for (const r of rows) (isBulk(r.market, bulk.under) ? drop : keep).push(r);
    return {
      shown: keep,
      bulkRows: drop,
      bulkValue: drop.reduce((s, r) => s + r.market * (rowPct(r) / 100) * r.qty, 0),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, bulk, pct]);

  const bulkQty = bulkRows.reduce((s, r) => s + r.qty, 0);

  const { marketTotal, grossTotal, payoutTotal, totalQty, feeAmount } = useMemo(() => {
    let m = 0, g = 0, q = 0;
    shown.forEach((r) => {
      m += r.market * r.qty;
      g += r.market * (rowPct(r) / 100) * r.qty;
      q += r.qty;
    });
    const fee = g * (fees / 100);
    return {
      marketTotal: m,
      grossTotal: g,
      feeAmount: fee,
      payoutTotal: Math.max(0, g - fee - shipping),
      totalQty: q,
    };
    // rowPct closes over pct, which is already a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, pct, fees, shipping]);

  /* Fees come off a sale. With nothing priced there is no sale, and a fee
     profile that outlives the stack — which is every reload now that it is
     remembered — made an empty screen announce "$0.00 gross, −$4.50 fees, YOU
     KEEP $0.00". Shipping is not owed on cards nobody has counted yet. */
  const charging = grossTotal > 0 && (fees > 0 || shipping > 0);

  /* × means the card is out, not hidden.
   *
   * It used to drop the row and leave the card in the stack, so the totals
   * fell, the card stayed, and the next press of "Price the stack" brought it
   * back. Removing the card removes the row with it, through the effect
   * below — one direction, one source of truth. */
  const removeRow = (id) => {
    const row = rows.find((r) => r.id === id);
    if (row?.itemKey) setItems((prev) => prev.filter((i) => i.key !== row.itemKey));
    setRows((rs) => rs.filter((r) => r.id !== id));
  };

  /* The priced table follows the stack.
   *
   * Two lists that can be edited separately will disagree, and this one
   * disagreed about money: a card taken out of the stack left its row and its
   * line total on screen, so an empty stack could still be "worth" $11.19.
   * Prices are not recomputed here — those need the network and a press of the
   * button — but a row for a card that is gone is not stale, it is wrong. */
  React.useEffect(() => {
    setRows((rs) => {
      const byKey = new Map(items.map((i) => [i.key, i]));
      const next = rs
        .filter((r) => !r.itemKey || byKey.has(r.itemKey))
        .map((r) => {
          const it = r.itemKey ? byKey.get(r.itemKey) : null;
          return it && it.qty !== r.qty ? { ...r, qty: it.qty } : r;
        });
      // Same rows, same objects: hand back the original so this cannot loop.
      const same = next.length === rs.length && next.every((r, n) => r === rs[n]);
      return same ? rs : next;
    });
  }, [items]);

  // Nobody pays over market for a card in a stack, and a stray extra digit
  // turning 80 into 800 is a mistake worth making impossible.
  const setRowPct = (id, v) =>
    setRows((rs) =>
      rs.map((r) =>
        r.id === id ? { ...r, pct: v === "" ? null : Math.min(100, Math.max(0, Number(v))) } : r
      )
    );

  return (
    <div className="stack">
      <div className="stack-split">
        {/* ── paste the stack ── */}
        <div className="stack-input">
          <div className="stack-label stack-label--withhelp">
            <span>
              THE STACK
              {/* Cards and copies are different numbers and were both called
                cards: one card at seven copies read "7 CARDS" here and
                "1 CARDS · 7 COPIES" in the results. */}
            {items.length > 0 && ` — ${plural(items.length, "CARD")}`}
            </span>
            <button
              type="button"
              className="stack-help-btn"
              aria-expanded={helpOpen}
              aria-controls="stack-format-help"
              onClick={() => setHelpOpen((v) => !v)}
              title="How this works"
              /* Its text is "i", which tells a screen reader nothing. */
              aria-label="How this works"
            >
              i
            </button>
          </div>

          {helpOpen && <FormatHelp isOnePiece={isOnePiece} />}

          <UndoBar pending={undo.pending} onUndo={undo.undo} verb="Cleared" />

          {adopted && (
            <p className="stack-notice" role="status">
              Updated from another tab.
            </p>
          )}
          {saveFailed && (
            <p className="stack-notice stack-notice--warn" role="status">
              This stack isn&rsquo;t being saved — storage on this device is full.
              It will be gone if you reload.
            </p>
          )}

          <StackBuilder
            items={items}
            onAdd={addItem}
            onBump={bumpItem}
            isOnePiece={isOnePiece}
          />

          <div className="stack-actions">
            <button className="btn btn-primary" onClick={priceStack} disabled={loading || !items.length}>
              {loading ? "Pricing…" : "Price the stack"}
            </button>
            {/* Clears at once and offers the stack back for a few seconds.
                This was a two-step confirm, put there the day the stack
                started surviving reloads and one click beside the primary
                button became the only thing that could lose it. A confirm
                interrupts every clear to guard against the rare wrong one;
                an undo costs the careful person nothing and gives the
                careless one the moment in which they notice. */}
            <button
              className="btn btn-secondary"
              onClick={clearStack}
              disabled={!items.length}
            >
              Clear
            </button>
          </div>

          {/* Behind the dev gate, because pasting a list is how this screen
              used to work and is still the fastest way in with forty cards on
              a table — but it is the old way, and putting it beside the new
              one would just be two doors into the same room. */}
          {devUnlocked && (
            <div className="stack-paste">
              <button
                type="button"
                className="stack-paste-open"
                onClick={() => setPasteOpen((v) => !v)}
                aria-expanded={pasteOpen}
              >
                {pasteOpen ? "▾" : "▸"} Paste a list (dev)
              </button>
              {pasteOpen && (
                <>
                  <textarea
                    className="stack-textarea"
                    value={pasteDraft}
                    onChange={(e) => setPasteDraft(e.target.value)}
                    placeholder={isOnePiece ? "OP13-118\nOP09-061 x2" : "Umbreon VMAX 215\nCharizard ex 199 x2"}
                    spellCheck={false}
                    aria-label="Paste a list of cards"
                  />
                  <button type="button" className="btn btn-secondary" onClick={loadPaste}>
                    Add these to the stack
                  </button>
                </>
              )}
            </div>
          )}

          {error && <p className="msg-error">{error}</p>}

          {/* Named, so a card missing from the total is never a surprise —
              and pointed at the manual entry below rather than left there. */}
          {/* "No match" was a lie once cards were picked rather than typed: a
              card you chose off the search obviously exists. What is missing
              is a price for that print. */}
          {unmatched.length > 0 && (
            <p className="msg-empty">
              No price for: {unmatched.map((m) => (m.number ? `${m.name} ${m.number}` : m.name)).join(", ")}
              {" "}— the market has no listing for that print. Add it manually below to
              count it at your own price.
            </p>
          )}

          <ManualCard onAdd={addManual} />
        </div>

        {/* ── the percentage + totals ── */}
        <div className="stack-verdict">
          <div className="stack-verdict-head">
            <span className="stack-label">SELLING AT</span>
            <span className="stack-label">OF TCG MARKET</span>
          </div>

          <div className="stack-pct-row">
            <div className="stack-pct">
              {/* Typeable, so a rate outside the usual band doesn't need the
                  slider. Kept as text so it can be styled at display size. */}
              <input
                className="stack-pct-input"
                type="text"
                inputMode="numeric"
                value={pctText}
                onChange={(e) => {
                  // Digits and one point. Everything else is not a number here.
                  let v = e.target.value.replace(/[^\d.]/g, "");
                  const parts = v.split(".");
                  if (parts.length > 2) v = `${parts[0]}.${parts.slice(1).join("")}`;

                  /* An empty box leaves the last rate standing rather than
                     dropping to zero: clearing it to type a new one used to
                     flash the whole stack to $0.00. Blur settles it. */
                  if (v === "" || v === ".") { setPctText(v); return; }

                  /* Over the ceiling, the box shows the ceiling. Letting it
                     read 999 while the stack priced at 200 would be the same
                     lie in the other direction. */
                  const n = clamp(v, 0, 200);
                  setPctText(Number(v) > 200 ? String(n) : v);
                  setPct(n);
                }}
                onBlur={() => {
                  const n = pctText === "" || pctText === "." ? PCT_MIN : clamp(pctText, 0, 200);
                  applyPct(n || PCT_MIN);
                }}
                aria-label="Percentage of market"
                size={3}
                /* Sized to the digits it is holding. A fixed 2.2ch fitted the
                   two the presets produce and clipped the third against the
                   container's edge, so 200% rendered as "?00%". */
                style={{ width: `${String(pct).length + 0.3}ch` }}
              />
              <span>%</span>
            </div>
            <div className="stack-pct-controls">
              <input
                type="range"
                className="stack-slider"
                min={PCT_MIN}
                max={PCT_MAX}
                step={5}
                value={Math.min(PCT_MAX, Math.max(PCT_MIN, pct))}
                onChange={(e) => applyPct(Number(e.target.value))}
                aria-label="Percentage of market slider"
              />
              <div className="stack-presets">
                {PRESETS.map((v) => (
                  <button
                    key={v}
                    className={"stack-preset" + (v === pct ? " stack-preset--active" : "")}
                    aria-pressed={v === pct}
                    onClick={() => applyPct(v)}
                  >
                    {v}%
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="stack-fees">
            <label className="stack-fee">
              <span className="stack-label">FEES %</span>
              {/* min and max are the spinner's business and the form's; neither
                  runs on a typed character. 150% fees and -$20 shipping were
                  both accepted, and negative fees paid you more than the cards
                  were worth: gross $6.12, "you keep $16.94". */}
              <input
                type="number" min={0} max={100} step={0.1}
                value={fees}
                onChange={(e) => setFees(clamp(e.target.value, 0, 100))}
              />
            </label>
            <label className="stack-fee">
              <span className="stack-label">SHIPPING $</span>
              <input
                type="number" min={0} step={0.5}
                value={shipping}
                onChange={(e) => setShipping(clamp(e.target.value, 0, Infinity))}
              />
            </label>
            <div className="stack-fee-presets">
              {/* A preset reads as selected when the state it would set is the
                  state you're already in — so typing 12.6 by hand lights TCG
                  too, and typing anything else lights none of them. */}
              {FEE_PRESETS.map((p) => {
                const active = fees === p.fees && (p.shipping == null || shipping === p.shipping);
                return (
                  <button
                    key={p.label}
                    className={"stack-preset" + (active ? " stack-preset--active" : "")}
                    aria-pressed={active}
                    onClick={() => {
                      setFees(p.fees);
                      if (p.shipping != null) setShipping(p.shipping);
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="stack-totals">
            <div>
              <div className="stack-label">MARKET TOTAL</div>
              <div className="stack-total-market">{usd(marketTotal)}</div>
              {charging && (
                <div className="stack-breakdown">
                  <span>{usd(grossTotal)} gross</span>
                  <span>−{usd(feeAmount + shipping)} fees</span>
                </div>
              )}
            </div>
            <div>
              <div className="stack-label stack-label--accent">
                {charging ? "YOU KEEP" : "STACK IS WORTH"}
              </div>
              <div className="stack-total-payout">{usd(payoutTotal)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── line items ── */}
      {rows.length > 0 && (
        <>
          <div className="stack-meta">
            <span className="stack-label">
              {plural(shown.length, "CARD")} · {plural(totalQty, "COPY", "COPIES")} PRICED
              {manualCount > 0 && ` · ${manualCount} AT YOUR OWN PRICE`}
            </span>
            <BulkFilter
              hide={bulk.hide}
              under={bulk.under}
              onChange={changeBulk}
              hiddenCount={bulkRows.length}
            />
            {/* No hiddenValue here — the line below says it in full, and
                twice is just noise. */}
          </div>

          {bulk.hide && bulkRows.length > 0 && (
            <p className="msg-empty">
              {bulkRows.length} card{bulkRows.length === 1 ? "" : "s"}
              {bulkQty !== bulkRows.length && ` (${bulkQty} copies)`} under ${bulk.under} set
              aside, worth {usd(bulkValue)} at {pct}% — not counted in the total above.
            </p>
          )}

          <div className="stack-table">
            <div className="stack-row stack-row--head">
              <div />
              <div>CARD</div>
              <div>SET</div>
              <div className="ta-r">QTY</div>
              <div className="ta-r">MARKET</div>
              <div className="ta-r">AT %</div>
              <div className="ta-r">LINE TOTAL</div>
              <div />
            </div>

            {shown.map((r) => (
              <div className={r.manual ? "stack-row stack-row--manual" : "stack-row"} key={r.id}>
                {/* The lookup already returns art; the placeholder stays as the
                    fallback for anything that comes back without an image. */}
                {r.image ? (
                  <button
                    type="button"
                    className="stack-thumb-btn"
                    onClick={() => setZoom(r)}
                    title={`Enlarge ${r.name}`}
                    aria-label={`Enlarge ${r.name}`}
                  >
                    <img className="stack-thumb" src={r.image} alt="" loading="lazy" />
                  </button>
                ) : (
                  <div className="stack-thumb" />
                )}
                <div className="stack-name">
                  {r.name}
                  {r.manual && <span className="stack-manual-note">no market price</span>}
                  {/* A bare code names a card, not a print, so the row says how
                      many it passed over and lets you look. It lives in the
                      name column rather than beside the set, because the set
                      column is hidden on a phone — which left no way at all to
                      change print on the device most likely to be used at a
                      table. The print's own label rides along, since that is
                      hidden with the set column too. */}
                  {r.prints?.length > 1 && (
                    <button
                      type="button"
                      className="stack-print-pick"
                      onClick={() => setPicking(r)}
                      title="This card number has other prints — pick the one you're holding"
                    >
                      <span className="stack-print-pick-label">{r.set} · </span>
                      {r.prints.findIndex((p) => p.id === r.cardId) + 1} of {r.prints.length}
                    </button>
                  )}
                </div>
                <div className="stack-set">
                  {r.set} {r.number && `· ${r.number}`}
                </div>
                <div className="ta-r stack-mono">{r.qty}</div>
                <div className="ta-r stack-mono stack-dim">
                  {usd(r.market)}
                  {r.manual && <span className="stack-manual-tag" title="You set this price">YOURS</span>}
                </div>
                <div className="ta-r stack-each-cell">
                  <input
                    className={"stack-row-pct" + (r.pct != null ? " stack-row-pct--set" : "")}
                    type="number" min={0} max={100}
                    value={r.pct == null ? "" : r.pct}
                    placeholder={String(pct)}
                    /* An empty box follows the stack percentage, but the arrows
                       stepped from nothing and landed on 1. Seed it with the
                       figure it was already showing, so stepping continues
                       from what you can see. */
                    onFocus={() => { if (r.pct == null) setRowPct(r.id, pct); }}
                    onChange={(e) => setRowPct(r.id, e.target.value)}
                    title="Override this card's percentage"
                    /* Forty rows had forty spin buttons all called "Override
                       this card's percentage", each governing different money.
                       The last control on this screen still naming its job
                       instead of its subject. */
                    aria-label={`Percentage for ${r.name}${r.number ? `, ${r.number}` : ""}`}
                  />
                  <span className="stack-each">{usd(r.market * (rowPct(r) / 100))}</span>
                </div>
                <div className="ta-r stack-line">{usd(r.market * (rowPct(r) / 100) * r.qty)}</div>
                {/* A div with an onClick is not a control: no keyboard, no
                    screen reader, no focus. The stepper next door names the
                    card it acts on and this said nothing at all. */}
                <button
                  type="button"
                  className="ta-r stack-remove"
                  onClick={() => removeRow(r.id)}
                  /* Not "take out", which is what the stepper next door is
                     already called at a count of one — two controls answering
                     to one name is its own kind of unusable. And named down to
                     the set and the price, because two cards added by hand can
                     share a name, which brings the same collision back from
                     the other side. */
                  aria-label={`Remove ${r.name}, ${r.set}, ${usd(r.market)}, from the stack`}
                  title={`Remove ${r.name} from the stack`}
                >
                  ×
                </button>
              </div>
            ))}

            <div className="stack-row stack-row--foot">
              <div />
              <div className="stack-name">Stack total</div>
              <div />
              <div className="ta-r stack-mono stack-dim">{totalQty}</div>
              <div className="ta-r stack-mono stack-dim">{usd(marketTotal)}</div>
              <div />
              {/* The sum of the column it sits under, which it was not: this
                  cell showed the figure after fees and shipping, so it agreed
                  with the lines above it only when both were zero. What comes
                  off is worth saying, but underneath, and named. */}
              <div className="ta-r stack-grand">
                {usd(grossTotal)}
                {(fees > 0 || shipping > 0) && (
                  <span className="stack-grand-net">{usd(payoutTotal)} after fees</span>
                )}
              </div>
              <div />
            </div>
          </div>
        </>
      )}

      {/* The picker itself. Art large enough to recognise, price beside it —
          those are the only two things that tell prints apart, since the
          catalogue leaves rarity blank on most of them. */}
      {picking && (
        <div className="card-zoom print-picker" onClick={() => setPicking(null)}>
          <div className="card-zoom-panel print-picker-panel" onClick={(e) => e.stopPropagation()}>
            <div className="print-picker-head">
              <div>
                <div className="card-zoom-name">{picking.name}</div>
                <div className="card-zoom-set">{picking.number} · which one are you holding?</div>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setPicking(null)}>Close</button>
            </div>
            <div className="print-picker-grid">
              {picking.prints.map((p) => (
                <button
                  key={p.id}
                  className={"print-option" + (p.id === picking.cardId ? " print-option--on" : "")}
                  onClick={() => { choosePrint(picking.id, p.id); setPicking(null); }}
                >
                  <img src={p.image} alt={p.label} loading="lazy" />
                  <span className="print-option-label">{p.label}</span>
                  <span className="print-option-price">
                    {p.market > 0 ? usd(p.market) : "no price"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <CardZoom
        card={zoom && { ...zoom, imageUrl: zoom.image }}
        onClose={() => setZoom(null)}
      />
    </div>
  );
}
